import { useContext } from "react";
import type { CSSProperties } from "react";
import type { Dispatcher } from "../studio-state";
import {
  bug,
  DEFAULT_CONFIG as APPKIT_CONFIG,
  focusStyle as appkitFocusStyle,
} from "@opencast/appkit";


/** Shortcuts for colors, avoids accidental typos in typing CSS variable names. */
export const COLORS = APPKIT_CONFIG.colors;

/** Breakpoint values */
export const BREAKPOINTS = APPKIT_CONFIG.breakpoints;

export const focusStyle = (options?: Parameters<typeof appkitFocusStyle>[1], color?: string) => {
  let config = APPKIT_CONFIG;
  if (color) {
    config = {
      colors: { ...APPKIT_CONFIG.colors, focus: color },
      breakpoints: APPKIT_CONFIG.breakpoints,
    };
  }
  return appkitFocusStyle(config, options);
};

/**
 * Checks if we app is running on a mobile device.
 *
 * This check could be more exhaustive, but this includes all browser we
 * officially support.
 */
export const onMobileDevice = () =>
  /Android|iPhone|iPad|iPod/i.test(navigator.platform) ||
  /Android/i.test(navigator.userAgent);

/**
 * Checks if the client supports capturing the device's display (or individual
 * windows).
 *
 * Detecting whether display capture is supported is hard. There is currently
 * no proper solution. See these two links for more information:
 * - https://stackoverflow.com/q/58842831/2408867
 * - https://github.com/w3c/mediacapture-screen-share/issues/127
 *
 * To work around this problem, we simply check if the browser runs on a
 * mobile device. Currently, no mobile device/browser supports display
 * capture. However, this will probably change in the future, so we have to
 * revisit this issue again. This is tracked in this issue:
 * https://github.com/opencast/studio/issues/204
 */
export const isDisplayCaptureSupported = () =>
  "mediaDevices" in navigator &&
  "getDisplayMedia" in navigator.mediaDevices &&
  !onMobileDevice();

/**
 * Checks if the client supports capturing "user devices" (usually webcams or
 * phone cameras).
 */
export const isUserCaptureSupported = () =>
  "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices;

/**
 * Checks if the browsers supports the `MediaRecorder` API required to actually
 * record the media streams.
 */
export const isRecordingSupported = () => typeof MediaRecorder !== "undefined";

/**
 * Checks if this runs in Safari. Check from https://stackoverflow.com/a/23522755/
 */
export const onSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Best-effort check for GPU-accelerated WebGL support.
 */
let cachedWebGL2Available: boolean | undefined;

export const isWebGL2Available = () => {
  if (cachedWebGL2Available !== undefined) {
    return cachedWebGL2Available;
  }

  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2", { preserveDrawingBuffer: false });
    cachedWebGL2Available = !!gl;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    return cachedWebGL2Available;
  } catch {
    cachedWebGL2Available = false;
    return false;
  }
};

/**
 * Checks whether the 2D canvas `filter` API is supported. We use this to
 * determine whether `ctx.filter = 'blur(...)'` will have an effect.
 */
let cachedCanvas2DFilterAvailable: boolean | undefined;

export const isCanvas2DFilterAvailable = () => {
  if (cachedCanvas2DFilterAvailable !== undefined) {
    return cachedCanvas2DFilterAvailable;
  }

  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const maybe = ctx as unknown as { filter?: unknown } | null;
    cachedCanvas2DFilterAvailable = !!ctx && typeof (maybe?.filter) === "string";
    return cachedCanvas2DFilterAvailable;
  } catch {
    cachedCanvas2DFilterAvailable = false;
    return false;
  }
};

/**
 * Probes WebGL for GPU vendor/renderer info and software-rasterizer detection.
 * Result is cached at module level so the context is created exactly once.
 */
export type WebGLRendererInfo = {
  vendor: string;
  renderer: string;
  maxTextureSize: number;
  softwareRasterizer: boolean;
};

let cachedWebGLRendererInfo: WebGLRendererInfo | null | undefined;

export const getWebGLRendererInfo = (): WebGLRendererInfo | null => {
  if (cachedWebGLRendererInfo !== undefined) {
    return cachedWebGLRendererInfo;
  }
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) {
      cachedWebGLRendererInfo = null;
      return null;
    }
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = String(ext
      ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR));
    const renderer = String(ext
      ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER));
    const maxTextureSize = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE));
    const softwareRasterizer = /swiftshader|llvmpipe|mesa software|softpipe/i.test(renderer);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    cachedWebGLRendererInfo = { vendor, renderer, maxTextureSize, softwareRasterizer };
    return cachedWebGLRendererInfo;
  } catch {
    cachedWebGLRendererInfo = null;
    return null;
  }
};

/**
 * Returns the dimensions as [w, h] array or `null` if there is no video track.
 */
export const dimensionsOf = (stream: MediaStream | null): [number, number] | null => {
  const track = stream?.getVideoTracks()?.[0];
  let settings: MediaTrackSettings | undefined;
  if (track && typeof track.getSettings === "function") {
    settings = track.getSettings();
  } else {
    settings = undefined;
  }
  let width: number | undefined = settings?.width ?? undefined;
  let height: number | undefined = settings?.height ?? undefined;

  if ((width == null || height == null) && stream) {
    // Fallback: some browsers don't populate track settings immediately — use
    // an intrinsic-dimensions hint written by the previewing <video> element.
    type StreamWithHint = MediaStream & { __videoDimensions?: [number, number] };
    const hint = (stream as StreamWithHint).__videoDimensions;
    if (hint && hint[0] && hint[1]) {
      width = hint[0];
      height = hint[1];
    }
  }
  return width == null || height == null ? null : [width, height];
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (
    callback: () => void | Promise<void>,
  ) => { finished: Promise<void>; ready: Promise<void>; skipTransition: () => void };
};

/**
 * Runs `update` inside a native View Transition (when supported) so that
 * layout changes it causes (elements appearing, disappearing, resizing or
 * moving) animate smoothly instead of snapping instantly. Falls back to
 * calling `update` directly on unsupported browsers or when the user prefers
 * reduced motion. `update` may return a promise; the transition's "after"
 * state is captured once it resolves (or after `timeoutMs`, whichever is
 * first, so a slow async step like a permission prompt can't freeze the UI).
 */
export const withViewTransition = (update: () => void | Promise<void>, timeoutMs = 4000): void => {
  const doc = document as ViewTransitionDocument;
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  if (typeof doc.startViewTransition !== "function" || prefersReducedMotion) {
    void update();
    return;
  }

  try {
    doc.startViewTransition(() => Promise.race([
      Promise.resolve(update()),
      new Promise<void>(resolve => window.setTimeout(resolve, timeoutMs)),
    ]));
  } catch (_e) {
    void update();
  }
};

/**
 * Inline style assigning a stable `view-transition-name` to an element so
 * `withViewTransition` can animate it moving/resizing/appearing/disappearing.
 */
export const viewTransitionStyle = (name: string): CSSProperties => ({ viewTransitionName: name });

/** Converts the MIME type into a file extension. */
export const mimeToExt = (mime: string) => {
  if (mime) {
    const lowerMime = mime.toLowerCase();
    if (lowerMime.startsWith("video/webm")) {
      return "webm";
    }
    if (lowerMime.startsWith("video/mp4")) {
      return "mp4";
    }
    if (lowerMime.startsWith("video/x-matroska")) {
      return "mkv";
    }
    if (lowerMime.startsWith("video/avi")) {
      return "avi";
    }
    if (lowerMime.startsWith("video/quicktime")) {
      return "mov";
    }
  }

  // If we know nothing, our best guess is webm; except for Safari which does
  // not understand webm: there it's mp4.
  return onSafari() ? "mp4" : "webm";
};

/**
 * Returns a suitable filename for a recording with the MIME type `mime` and the
 * given `flavor`. The latter should be either `presenter` or `presentation`.
 * `mime` can be null or a string and is converted to a file extension on a best
 * effort basis.
 */
export const recordingFileName = ({ mime, flavor, title, presenter }: {
  mime: string;
  flavor: string;
  title: string;
  presenter: string;
}) => {
  const titlePart = (title ? ` - ${title}` : "").substring(0, 50);
  const presenterPart = (presenter ? ` - ${presenter}` : "").substring(0, 50);
  const ext = mimeToExt(mime);
  const flavorPart = ` (${flavor}, Mediasite Capture)`;
  return `${nowAsString()}${titlePart}${presenterPart}${flavorPart}.${ext}`;
};

const nowAsString = () => {
  const pad2 = (n: number) => (n >= 10 ? "" : "0") + n;

  const now = new Date();
  return ""
    + now.getFullYear() + "-"
    + pad2(now.getMonth() + 1) + "-"
    + pad2(now.getDate()) + " "
    + pad2(now.getHours()) + "-"
    + pad2(now.getMinutes());
};

export const userHasWebcam = async () => {
  if (!("mediaDevices" in navigator)) {
    return false;
  }

  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.some(d => d.kind === "videoinput");
};

/**
 * Decodes the given hex string into a new string. If the given string contains
 * characters that are not hexadecimal digits or if the string's length is odd,
 * this function will throw an exception.
 */
export const decodeHexString = (hex: string): string => {
  if (hex.length % 2 !== 0) {
    throw new SyntaxError("hex string does not have an even length");
  }

  const digitToNum = (digit: string) => {
    if (digit >= "0" && digit <= "9") {
      return digit.charCodeAt(0) - "0".charCodeAt(0);
    } else if (digit >= "a" && digit <= "f") {
      return digit.charCodeAt(0) - "a".charCodeAt(0) + 10;
    } else if (digit >= "A" && digit <= "F") {
      return digit.charCodeAt(0) - "A".charCodeAt(0) + 10;
    } else {
      throw new RangeError(`invalid hex digit '${digit}'`);
    }
  };

  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = 16 * digitToNum(hex.substring(i, i + 1))
      + digitToNum(hex.substring(i + 1, i + 2));
  }

  return new TextDecoder().decode(bytes);
};

/** Returns a promise that resolves after `ms` milliseconds. */
export const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/** Obtains all media devices and stores them into the global state. */
export const queryMediaDevices = async (dispatch: Dispatcher) => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  dispatch({ type: "UPDATE_MEDIA_DEVICES", devices });
};

/**
 * Filters the `allDevices` array such that only devices with the given `kind`
 * are included and no two devices have the same `deviceId`.
 */
export const getUniqueDevices = (
  allDevices: MediaDeviceInfo[],
  kind: MediaDeviceKind,
): MediaDeviceInfo[] => {
  const out: MediaDeviceInfo[] = [];
  for (const d of allDevices) {
    // Only interested in one kind of device.
    if (d.kind !== kind) {
      continue;
    }

    // If we already have a device with that device ID, we ignore it.
    if (out.some(od => od.deviceId === d.deviceId)) {
      continue;
    }

    out.push(d);
  }

  return out;
};

/** Like `useContext` but throws an error if the context is `null`. */
export const usePresentContext = <T, >(context: React.Context<T | null>, hookName: string): T => {
  return useContext(context)
    ?? bug(`hook '${hookName}' called without parent context provider`);
};

export interface MTHeader {
  alg: string;
  typ: string;
}

export interface MTPayload {
  ctx: string;
  iat: number;
  nbf: number;
  exp: number;
  aud: string;
  iss: string;
  sub: string;
  moid: string;
  siteid: string;
  sitename: string;
  upid: string;
  user: string;
}

export const decodeJwt = (token: string | null): { header: MTHeader; payload: MTPayload } | null => {
  if (token === null) {
    return null;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new SyntaxError("Invalid JWT format");
    }

    const headerEncoded = parts[0];
    const payloadEncoded = parts[1];

    const header = JSON.parse(decodeBase64(headerEncoded)) as MTHeader;
    const payload = JSON.parse(decodeBase64(payloadEncoded)) as MTPayload;

    return {
      header: header,
      payload: payload,
    };
  } catch (error: unknown) {
    console.error("Error decoding JWT:", (error as Error).message);
    return null;
  }
};

export const decodeBase64 = (encodedString: string): string => {
  try {
    const decodedString = atob(encodedString.replace(/-/g, "+").replace(/_/g, "/"));
    return decodedString;
  } catch {
    throw new Error("Failed to decode base64 string");
  }
};

export const isNullUndefinedOrEmpty = (value: string | null | undefined): boolean => {
  return value === null || value === undefined || value.trim() === "";
};

/**
 * Convert an arbitrary string (often a localized label) into a safe
 * kebab-case class/id fragment suitable for stable DOM class names.
 */
export const toClassId = (s: string): string =>
  s
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9\s-]/gi, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
